import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Shield, ShieldOff, Trash2, UserCog, MoreVertical, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import AdminUserDrawer from "@/pages/admin/AdminUserDrawer";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [menu, setMenu] = useState(null);
  const [drawer, setDrawer] = useState(null);

  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(t) || u.email.toLowerCase().includes(t));
  }, [users, q]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); setMenu(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setRole = (u) => act(() => api.patch(`/admin/users/${u.user_id}/role`, { role: u.role === "admin" ? "user" : "admin" }), "Role updated");
  const suspend = (u) => act(() => api.post(`/admin/users/${u.user_id}/suspend`), "User suspended");
  const reactivate = (u) => act(() => api.post(`/admin/users/${u.user_id}/reactivate`), "User reactivated");
  const remove = (u) => { if (window.confirm(`Delete ${u.email}? This cannot be undone.`)) act(() => api.delete(`/admin/users/${u.user_id}`), "User deleted"); };

  return (
    <div data-testid="admin-users">
      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 text-[#71717A] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" data-testid="admin-user-search"
          className="bg-[#121214] border-white/10 text-white pl-9 h-10" />
      </div>
      <div className="rounded-2xl border border-white/8 bg-[#121214] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead><tr className="border-b border-white/8 text-left text-[#71717A]">
              <th className="px-5 py-3 font-normal">Name</th><th className="px-5 py-3 font-normal">Email</th>
              <th className="px-5 py-3 font-normal">Role</th><th className="px-5 py-3 font-normal">Plan</th>
              <th className="px-5 py-3 font-normal">Status</th><th className="px-5 py-3 font-normal w-10"></th>
            </tr></thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.user_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid="admin-user-row">
                  <td className="px-5 py-3 text-white">{u.name || "—"}</td>
                  <td className="px-5 py-3 text-[#A1A1AA]">{u.email}</td>
                  <td className="px-5 py-3"><span className={`capitalize ${u.role === "admin" ? "text-[#D4AF37]" : "text-[#A1A1AA]"}`}>{u.role}</span></td>
                  <td className="px-5 py-3 text-[#D4AF37] capitalize">{u.subscription?.tier || "free"}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.suspended ? "bg-red-500/15 text-red-400" : u.subscription?.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-[#71717A]"}`}>
                      {u.suspended ? "Suspended" : u.subscription?.status === "active" ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="px-5 py-3 relative">
                    <button onClick={() => setMenu(menu === u.user_id ? null : u.user_id)} data-testid={`admin-user-menu-${u.user_id}`} className="text-[#71717A] hover:text-white"><MoreVertical className="w-4 h-4" /></button>
                    {menu === u.user_id && (
                      <div className="absolute right-5 top-10 z-20 w-44 rounded-xl border border-white/10 bg-[#1C1C1F] py-1.5 shadow-xl" onMouseLeave={() => setMenu(null)}>
                        <button onClick={() => { setDrawer(u.user_id); setMenu(null); }} data-testid={`admin-view-${u.user_id}`} className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-[#D4D4D8] hover:bg-white/5"><Eye className="w-4 h-4" /> View details</button>
                        <button onClick={() => setRole(u)} className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-[#D4D4D8] hover:bg-white/5"><UserCog className="w-4 h-4" /> {u.role === "admin" ? "Make user" : "Make admin"}</button>
                        {u.suspended
                          ? <button onClick={() => reactivate(u)} data-testid={`admin-reactivate-${u.user_id}`} className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-[#D4D4D8] hover:bg-white/5"><Shield className="w-4 h-4" /> Reactivate</button>
                          : <button onClick={() => suspend(u)} data-testid={`admin-suspend-${u.user_id}`} className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-[#D4D4D8] hover:bg-white/5"><ShieldOff className="w-4 h-4" /> Suspend</button>}
                        <button onClick={() => remove(u)} data-testid={`admin-delete-${u.user_id}`} className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-red-400 hover:bg-white/5"><Trash2 className="w-4 h-4" /> Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-[#52525B] mt-3">{filtered.length} of {users.length} users</p>
      {drawer && <AdminUserDrawer userId={drawer} onClose={() => setDrawer(null)} onChanged={load} />}
    </div>
  );
}
